import React, { useState, useEffect } from "react";
import { createPageUrl } from "@/utils";
import { db } from "@/api/db";
import { Play, Lock, CheckCircle, Calendar, ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

function getEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  return url;
}

export default function TrackDetail() {
  const urlParams = new URLSearchParams(window.location.search);
  const trackId = urlParams.get("id");

  const [track, setTrack] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [user, setUser] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (trackId) loadData();
  }, [trackId]);

  async function loadData() {
    const u = await db.auth.me();
    setUser(u);
    const [trackData, lsns] = await Promise.all([
      db.entities.CareerTrack.filter({ id: trackId }),
      db.entities.Lesson.filter({ track_id: trackId, is_published: true }, "order"),
    ]);
    setTrack(trackData[0] || null);
    setLessons(lsns);

    const prog = await db.entities.UserProgress.filter({ user_email: u.email, track_id: trackId });
    setProgress(prog);

    const firstLesson = lsns.find(l => l.video_url);
    if (firstLesson) setActiveVideo({ url: firstLesson.video_url, title: firstLesson.title });

    setLoading(false);
  }

  const completedLessonIds = new Set(progress.map(p => p.lesson_id));
  const isSubscriber = !!user;

  async function markComplete(lesson) {
    if (!isSubscriber || completedLessonIds.has(lesson.id)) return;
    await db.entities.UserProgress.create({
      user_email: user.email,
      lesson_id: lesson.id,
      track_id: lesson.track_id,
      completed_at: new Date().toISOString(),
    });
    setProgress(prev => [...prev, { lesson_id: lesson.id }]);
  }

  function openLesson(lesson) {
    markComplete(lesson);
    setActiveVideo({ url: lesson.video_url, title: lesson.title });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: '#00DCA4', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!track) return (
    <div className="text-center py-16" style={{ color: '#99A6AB' }}>Módulo não encontrado.</div>
  );

  const completedCount = lessons.filter(l => completedLessonIds.has(l.id)).length;

  return (
    <div className="space-y-6">
      <Link to={createPageUrl("Tracks")} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: '#646C6F' }}>
        <ArrowLeft className="w-3.5 h-3.5" /> Voltar aos módulos
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4">
        {track.image_url && (
          <img src={track.image_url} alt={track.title} className="w-14 h-14 rounded-2xl object-cover flex-shrink-0" />
        )}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold"
              style={{ fontFamily: 'Poppins, sans-serif', color: '#2E3233' }}>{track.title}</h1>
            <Badge className="border-0"
              style={{ background: '#EAFAF6', color: '#00664C' }}>{track.category}</Badge>
          </div>
          <p className="mt-1" style={{ color: '#646C6F' }}>{track.description}</p>
          <p className="text-sm mt-1" style={{ color: '#99A6AB' }}>
            {completedCount}/{lessons.length} aulas concluídas
          </p>
        </div>
      </div>

      {/* Subscription gate */}
      {!isSubscriber && (
        <div className="rounded-2xl p-4 flex items-center gap-3"
          style={{ background: '#FEEDB4', borderLeft: '4px solid #B2911E' }}>
          <Lock className="w-5 h-5 flex-shrink-0" style={{ color: '#B2911E' }} />
          <div className="flex-1">
            <p className="font-medium text-sm" style={{ color: '#65541B' }}>
              Assine para acessar o conteúdo completo
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#65541B' }}>
              Você pode ver a estrutura, mas as aulas estão bloqueadas.
            </p>
          </div>
          <Link to={createPageUrl("Subscription")}>
            <button className="text-xs font-bold px-4 py-2 flex-shrink-0"
              style={{ background: '#00DCA4', color: '#003326', borderRadius: '100px' }}>
              Assinar
            </button>
          </Link>
        </div>
      )}

      {/* Main content: player + sidebar */}
      <div className={`flex gap-6 ${activeVideo ? "flex-col lg:flex-row lg:items-start" : ""}`}>

        {/* Video Player */}
        {activeVideo && (
          <div className="flex-1 min-w-0 lg:sticky lg:top-4">
            <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: '#1A1D1E' }}>
              <div className="px-4 py-3" style={{ background: '#1A1D1E' }}>
                <p className="text-white text-sm font-medium truncate">{activeVideo.title}</p>
              </div>
              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  key={activeVideo.url}
                  src={getEmbedUrl(activeVideo.url)}
                  title={activeVideo.title}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        )}

        {/* Lesson List */}
        <div className={activeVideo ? "w-full lg:w-96 flex-shrink-0" : "w-full"}>
          <div className="rounded-xl overflow-hidden shadow-sm" style={{ background: '#fff', border: '1px solid #E5E9EA' }}>
            <div className="px-5 py-3" style={{ borderBottom: '1px solid #E5E9EA' }}>
              <p className="font-semibold text-sm" style={{ color: '#2E3233' }}>Aulas</p>
            </div>
            {lessons.length === 0 ? (
              <div className="text-center py-12" style={{ color: '#99A6AB' }}>
                <p>Nenhuma aula disponível neste módulo ainda.</p>
              </div>
            ) : (
              lessons.map(lesson => {
                if (lesson.type === "section") {
                  return (
                    <div key={lesson.id} className="px-5 py-2.5"
                      style={{ background: '#F8F9FA', borderBottom: '1px solid #E5E9EA' }}>
                      <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#646C6F' }}>
                        {lesson.title}
                      </p>
                    </div>
                  );
                }
                const done = completedLessonIds.has(lesson.id);
                const isActive = activeVideo?.url === lesson.video_url;
                return (
                  <div
                    key={lesson.id}
                    className="flex items-center gap-3 px-5 py-3 transition-colors cursor-pointer"
                    style={{ background: isActive ? '#EAFAF6' : '#fff', borderBottom: '1px solid #F2F4F4' }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F2F4F4'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff'; }}
                    onClick={() => isSubscriber && lesson.video_url && openLesson(lesson)}
                  >
                    <div className="flex-shrink-0">
                      {done ? (
                        <CheckCircle className="w-4 h-4" style={{ color: '#00DCA4' }} />
                      ) : isActive ? (
                        <Play className="w-4 h-4" style={{ color: '#00664C', fill: '#00664C' }} />
                      ) : lesson.type === "live" ? (
                        <Calendar className="w-4 h-4" style={{ color: '#D9DBDA' }} />
                      ) : (
                        <Play className="w-4 h-4" style={{ color: '#D9DBDA' }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate"
                        style={{
                          color: isActive ? '#00664C' : done ? '#99A6AB' : '#2E3233'
                        }}>{lesson.title}</p>
                      {lesson.duration_minutes && (
                        <p className="text-xs" style={{ color: '#99A6AB' }}>{lesson.duration_minutes} min</p>
                      )}
                    </div>
                    {!isSubscriber && <Lock className="w-3 h-3 flex-shrink-0" style={{ color: '#D9DBDA' }} />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
